import React, { useState } from 'react';
import styled from 'styled-components';
import { Tooltip, Typography } from 'antd';

// Одна строка кнопки экрана. Используется и на карточке схемы, и в списке кнопок панели,
// чтобы одна и та же кнопка выглядела одинаково в обоих местах.
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ $active, $dropping }) => {
    if ($dropping) return '#f6ffed';
    return $active ? '#e6f4ff' : '#fafafa';
  }};
  border: 1px ${({ $dropping }) => ($dropping ? 'dashed' : 'solid')}
    ${({ $active, $dropping }) => {
    if ($dropping) return '#52c41a';
    return $active ? '#1890ff' : '#f0f0f0';
  }};
  box-shadow: ${({ $broken }) => ($broken ? 'inset 3px 0 0 #ff4d4f' : 'none')};
  transition: background .15s ease, border-color .15s ease;

  &:hover {
    background: ${({ $active }) => ($active ? '#d6ebff' : '#f0f7ff')};
    border-color: #1890ff;
  }
`;

const Icon = styled.img`
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  object-fit: contain;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background: #fff;
  padding: 2px;
`;

const NoIcon = styled.div`
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 1px dashed #e0e0e0;
  border-radius: 4px;
  color: #d0d0d0;
  font-size: 10px;
  line-height: 34px;
  text-align: center;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-weight: 500;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Доп. текст кнопки — то, что видит клиент второй строкой. Держим одной строкой
// с многоточием, полностью показываем в подсказке
const Extra = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: #8c8c8c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function ButtonRow({
  name, targetLabel, extraText, icon, showIconSlot, isBroken, isActive, onClick, handle, onIconDrop,
}) {
  const [isDropping, setIsDropping] = useState(false);

  // Картинку можно бросить прямо на кнопку. Родные drag-события браузера не пересекаются
  // с перетаскиванием строк (оно на мышиных событиях), поэтому одно другому не мешает.
  const dropHandlers = onIconDrop ? {
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDropping(true);
    },
    onDragLeave: () => setIsDropping(false),
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDropping(false);
      onIconDrop(e.dataTransfer?.files?.[0]);
    },
  } : {};
  const row = (
    <Row
      $active={isActive}
      $broken={isBroken}
      $dropping={isDropping}
      onClick={onClick}
      role="presentation"
      {...dropHandlers}
    >
      {handle}
      {icon && <Icon src={icon} alt="" />}
      {!icon && (showIconSlot || isDropping) && (
        <NoIcon>{isDropping ? '↓' : 'нет'}</NoIcon>
      )}
      <Body>
        <Name>{name}</Name>
        {extraText && <Extra>{extraText}</Extra>}
        {targetLabel && (
          <Typography.Text
            type={isBroken ? 'danger' : 'secondary'}
            style={{ fontSize: 12, lineHeight: 1.4 }}
          >
            {targetLabel}
          </Typography.Text>
        )}
      </Body>
    </Row>
  );

  return extraText
    ? <Tooltip title={extraText} placement="top">{row}</Tooltip>
    : row;
}

export default ButtonRow;
